import { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Wallet, Plus, Pencil, Trash2, Save, X, Star, RotateCcw, Lock,
  TrendingUp, Calendar,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';

const fmt = (n) => Math.round(n || 0).toLocaleString('es-CL');

const empty = {
  codigo: '',
  etiqueta: '',
  usuario_email: '',
  trabajador_id_rrhh: '',
  trabajador_nombre: '',
  monto_asignado: 0,
  fecha_apertura: new Date().toISOString().slice(0, 10),
  observaciones: '',
  activo: true,
  favorita: false,
};

export default function CajasRendirPage() {
  const [cajas, setCajas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/cajas-rendir', { params: { incluir_inactivas: showInactive } });
      setCajas(res.data || []);
    } catch (err) {
      toast.error('Error cargando cajas');
    }
    setLoading(false);
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c.codigo);
    setForm({
      codigo: c.codigo,
      etiqueta: c.etiqueta || '',
      usuario_email: c.usuario_email || '',
      trabajador_id_rrhh: c.trabajador_id_rrhh || '',
      trabajador_nombre: c.trabajador_nombre || '',
      monto_asignado: c.monto_asignado || 0,
      fecha_apertura: c.fecha_apertura || '',
      observaciones: c.observaciones || '',
      activo: c.activo !== false,
      favorita: !!c.favorita,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.codigo.trim() || !form.etiqueta.trim()) {
      toast.error('Código y etiqueta son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, monto_asignado: parseFloat(form.monto_asignado) || 0 };
      if (editing) {
        await api.put(`/cajas-rendir/${editing}`, body);
        toast.success('Caja actualizada');
      } else {
        await api.post('/cajas-rendir', body);
        toast.success('Caja creada');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  const desactivar = async (codigo) => {
    if (!window.confirm(`¿Desactivar la caja ${codigo}? No aparecerá más en el FormaPagoSelector.`)) return;
    try {
      await api.delete(`/cajas-rendir/${codigo}`);
      toast.success('Caja desactivada');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const reactivar = async (codigo) => {
    try {
      await api.post(`/cajas-rendir/${codigo}/reactivar`);
      toast.success('Reactivada');
      load();
    } catch (err) {
      toast.error('Error');
    }
  };

  const cerrar = async (codigo) => {
    if (!window.confirm(`¿Cerrar la caja ${codigo}? Se marca como cerrada (no se podrán cargar más OCs nuevas).`)) return;
    try {
      await api.post(`/cajas-rendir/${codigo}/cerrar`);
      toast.success('Caja cerrada');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="cajas-rendir-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wallet className="w-6 h-6 text-amber-600" />
            Cajas a Rendir
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Medio de pago alternativo a TC. Aparecen en el selector de "Forma de Pago" del flujo de OC Express.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="chk-show-inactive" />
            Mostrar inactivas
          </label>
          <Button onClick={openNew} className="gap-1" data-testid="btn-new-caja">
            <Plus className="w-4 h-4" /> Nueva caja
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-center w-12">★</TableHead>
              <TableHead>Código</TableHead>
              <TableHead>Etiqueta</TableHead>
              <TableHead>Responsable</TableHead>
              <TableHead className="text-right">Monto asignado</TableHead>
              <TableHead className="text-right">Saldo actual</TableHead>
              <TableHead>Apertura</TableHead>
              <TableHead className="text-center">Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cajas.map((c) => (
              <TableRow key={c.codigo} className={c.activo === false ? 'opacity-60' : ''} data-testid={`row-caja-${c.codigo}`}>
                <TableCell className="text-center">
                  {c.favorita ? <Star className="h-4 w-4 text-amber-500 fill-amber-400 mx-auto" /> : <Star className="h-3.5 w-3.5 text-gray-300 mx-auto" />}
                </TableCell>
                <TableCell className="font-mono font-semibold">{c.codigo}</TableCell>
                <TableCell>{c.etiqueta}</TableCell>
                <TableCell className="text-xs">
                  {c.trabajador_nombre || '—'}
                  {c.usuario_email && <div className="text-gray-500">{c.usuario_email}</div>}
                </TableCell>
                <TableCell className="text-right font-mono">${fmt(c.monto_asignado)}</TableCell>
                <TableCell className="text-right font-mono">${fmt(c.saldo_actual)}</TableCell>
                <TableCell className="text-xs">{c.fecha_apertura}</TableCell>
                <TableCell className="text-center">
                  {c.estado === 'cerrada' ? (
                    <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded">Cerrada</span>
                  ) : c.activo === false ? (
                    <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Inactiva</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">Abierta</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} title="Editar" data-testid={`btn-edit-${c.codigo}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {c.estado !== 'cerrada' && c.activo !== false && (
                      <Button size="sm" variant="ghost" onClick={() => cerrar(c.codigo)} title="Cerrar caja" data-testid={`btn-close-${c.codigo}`}>
                        <Lock className="w-3.5 h-3.5 text-amber-700" />
                      </Button>
                    )}
                    {c.activo !== false ? (
                      <Button size="sm" variant="ghost" onClick={() => desactivar(c.codigo)} title="Desactivar" data-testid={`btn-deact-${c.codigo}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-600" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => reactivar(c.codigo)} title="Reactivar" data-testid={`btn-react-${c.codigo}`}>
                        <RotateCcw className="w-3.5 h-3.5 text-emerald-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {cajas.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-6 text-gray-500 text-sm">{loading ? 'Cargando...' : 'No hay cajas configuradas. Crea una con "Nueva caja".'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar caja' : 'Nueva caja a rendir'}</DialogTitle>
            <DialogDescription>Configura una caja con responsable y monto asignado. Aparecerá en el FormaPagoSelector.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Código*</Label>
              <Input value={form.codigo} onChange={(e) => setForm((p) => ({ ...p, codigo: e.target.value }))} placeholder="car1" disabled={!!editing} data-testid="input-codigo" />
              <p className="text-[10px] text-gray-500 mt-1">Convención: car1, car2…</p>
            </div>
            <div>
              <Label className="text-xs">Etiqueta*</Label>
              <Input value={form.etiqueta} onChange={(e) => setForm((p) => ({ ...p, etiqueta: e.target.value }))} placeholder="Caja Juan Pérez" data-testid="input-etiqueta" />
            </div>
            <div>
              <Label className="text-xs">Email responsable</Label>
              <Input value={form.usuario_email} onChange={(e) => setForm((p) => ({ ...p, usuario_email: e.target.value }))} placeholder="usuario@chirino.cl" data-testid="input-email" />
            </div>
            <div>
              <Label className="text-xs">Trabajador RRHH (opcional)</Label>
              <Input value={form.trabajador_nombre} onChange={(e) => setForm((p) => ({ ...p, trabajador_nombre: e.target.value }))} placeholder="Juan Pérez (RRHH)" data-testid="input-trabajador" />
            </div>
            <div>
              <Label className="text-xs">Monto asignado ($)</Label>
              <Input type="number" value={form.monto_asignado} onChange={(e) => setForm((p) => ({ ...p, monto_asignado: e.target.value }))} placeholder="500000" data-testid="input-monto" />
            </div>
            <div>
              <Label className="text-xs">Fecha apertura</Label>
              <Input type="date" value={form.fecha_apertura} onChange={(e) => setForm((p) => ({ ...p, fecha_apertura: e.target.value }))} data-testid="input-fecha" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Observaciones</Label>
              <Input value={form.observaciones} onChange={(e) => setForm((p) => ({ ...p, observaciones: e.target.value }))} placeholder="Notas internas" data-testid="input-obs" />
            </div>
            <div className="col-span-2 flex items-center gap-2 pt-1">
              <input id="caja-favorita" type="checkbox" checked={form.favorita} onChange={(e) => setForm((p) => ({ ...p, favorita: e.target.checked }))} className="accent-amber-500" data-testid="chk-favorita" />
              <Label htmlFor="caja-favorita" className="text-sm cursor-pointer flex items-center gap-1.5">
                <Star className="h-4 w-4 text-amber-500 fill-amber-400" />
                Marcar como favorita
                <span className="text-xs text-muted-foreground">(aparecerá como botón rápido en OC Express)</span>
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}><X className="w-4 h-4 mr-1" />Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save"><Save className="w-4 h-4 mr-1" />{saving ? 'Guardando…' : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
